import axios from "axios"
import React, { Component } from "react"
import { Button, Modal, Spinner } from "react-bootstrap"
import { getVault77ErrorMessage } from "src/components/vault77/core/apiError"
import { HealthRefreshContext, type HealthRefreshContextValue } from "src/components/vault77/health/HealthRefreshContext"
import { applyReviewToPayRate } from "src/components/vault77/payRateHistory/api/payRateHistory.api"
import AxiosConfig from "src/utilities/AxiosConfig"
import mutator from "src/utilities/mutator"
import snackBar from "src/utilities/snackBar/SnackBar"
import "./PayRatePrompt.css"

interface Props {
    reviewId: number
    employeeId: string | number
    manageParentState: Function
    getReviewApproval: Function
    addOrUpdateNotification?: Function
    getReviews: Function
    approveReview: Function
    // Persists the current pay model to the paired Vault77 review and returns its id.
    upsertVault77Review: () => Promise<number | null>
    // Re-fetches the open review so its lock state (HRApprovedDateUtc) updates immediately
    // after approval, instead of staying editable until the review is closed and reopened.
    refreshReview: () => void
}

interface State {
    loading: boolean
    eventType: string
}

class PayRatePrompt extends Component<Props, State> {

    // Approving a review changes the health picture: "Yes" applies the review to a new pay rate
    // (clears REVIEW_WITHOUT_PAY_RATE / MISSING_PAY_RATE), and "No" approves it without a rate (which
    // can newly raise REVIEW_WITHOUT_PAY_RATE). Both funnel through approveReview's success, so a single
    // refresh there keeps the Employees-page scan accurate. No-op outside the panel.
    static contextType = HealthRefreshContext

    state = {
        loading: false,
        eventType: ""
    }

    manageState = (keys: Array<{ key: string, value?: any }>) => {
        this.setState(mutator.mutate(this.state, keys))
    }

    // "Yes" — commit the pay change: make sure the proposed pay is saved on the
    // Vault77 review, then apply that review to create a PayRateHistory row
    // (auto-supersede + duplicate-date guard happen server-side), then approve.
    addPayRate = async () => {
        this.manageState([{ key: "loading", value: true }])
        try {
            const vault77ReviewId = await this.props.upsertVault77Review()
            if (!vault77ReviewId) {
                throw new Error("No pay rate to apply.")
            }
            await applyReviewToPayRate(vault77ReviewId)
            snackBar({ type: "success", text: "Pay Rate updated", timeout: 5000 })
            this.approveReview()
        } catch (err) {
            // No finally here: on success the prompt must stay open (spinner running) until
            // approveReview's chain finishes — its .finally() owns the cleanup for that path.
            this.manageState([{ key: "loading", value: false }])
            this.props.manageParentState([{ key: "PayRatePrompt", value: false }, { key: "loading", value: false }])
            snackBar({ type: "error", text: getVault77ErrorMessage(err, "Failed to update payrate"), timeout: 5000 })
        }
    }

    approveReview = () => {
        let url = "/api/employeereview/finalEmployeeReviewApproval/"
        url += `${this.props.reviewId}/noComment`
        axios.get(process.env.REACT_APP_API_URL + url, AxiosConfig.configValue("min"))
            .then((res) => {
                this.props.getReviewApproval()
            })
            .then(() => {
                if (this.props.addOrUpdateNotification) {
                    this.props.addOrUpdateNotification(true)
                }
                this.props.getReviews()
                // Refresh the open review so HRApprovedDateUtc lands in state now — this locks the
                // PayAdjuster and resets dirty/Save immediately, matching the close/reopen behavior.
                this.props.refreshReview()
                // Re-run the Employees-page health scan so the gauges + row warning reflect this
                // approval (a new pay rate applied, or a now-approved review with no rate).
                ;(this.context as HealthRefreshContextValue).notifyHealthChanged()
            })
            .catch((err) => {
                AxiosConfig.handleError(err)
                snackBar({ type: "error", text: "Failed to update payrate", timeout: 5000 })
            })
            .finally(() => {
                this.manageState([{ key: "loading", value: false }])
                this.props.manageParentState([{ key: "PayRatePrompt", value: false }, { key: "loading", value: false }])
            })
    }

    render() {

        const state = this.state

        return (
            <div className="PayRatePrompt">
                <Modal
                    id="add-employee-modal"
                    show={true}
                    backdrop="static"
                    keyboard={true}
                    centered={true}
                >
                    <Modal.Header>
                        <div className="PayRatePromptModalHeader">
                            <p>Update employee's pay rate?</p>
                        </div>
                    </Modal.Header>
                    <Modal.Footer>
                        <div className="PayRatePromptModalFooter">
                            {state.loading && <Spinner id="pay-rate-prompt-modal-loading-spinner" animation="border" />}
                            {!state.loading && <div className="PayRatePromptModalFooterControls">
                                <Button variant="secondary" onClick={() => this.approveReview()}>No</Button>
                                <Button onClick={() => this.addPayRate()} >Yes</Button>
                            </div>}
                        </div>
                    </Modal.Footer>
                </Modal>
            </div>
        )
    }
}

export default PayRatePrompt
